import config from '@/config';
import { ApiBase, InternationalizedField, ListOptions } from '@/api/common';
import ApiList from '@/api/list';
import { Mutation } from '@/store';
import store from '@/store';
import { bus } from '@/bus';
import { BusEvent } from '@/models/bus';
import Video from '@/api/video';
import InterfaceElement from '@/api/interfaceElement';

const NO_UPLOAD_FIELDS = [
  'SLUG',
  'icon_link',
  'icon_alt_link',
  'bg_image_link',
  'video_objects',
  'created',
  'updated',
  'theme_id'
];

export default class Skill extends ApiBase<Skill> {
  public static readonly SLUG: string = 'skill';
  public readonly SLUG: string = 'theme';
  public static readonly API_SLUG: string = 'theme';
  private static readonly URL_BASE: string = config.API_ROOT + '/theme/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public icon?: number | null = undefined;
  public icon_link?: string | null = undefined;
  public icon_alt?: number | null = undefined;
  public icon_alt_link?: string | null = undefined;
  public bg_image?: number | null = undefined;
  public bg_image_link?: string | null = undefined;
  public created?: string = undefined;
  public updated?: string = undefined;
  public theme_id?: string = undefined;
  public order?: number = undefined;
  public new?: boolean = undefined;
  public school?: number = undefined;
  public name: InternationalizedField<string> = new InternationalizedField<string>();
  public description: InternationalizedField<string> = new InternationalizedField<string>();
  public home_screen?: boolean = undefined;
  public videos?: number[] = undefined;
  public video_objects: Video[] = [];
  public user_interface_elements?: InterfaceElement[] = [];
  public parent_project?: any = undefined;

  // currently unused
  public spreadsheet?: string = undefined;

  // default is not belong to courses
  public is_skill_course: boolean = false; 

  public constructor() {
    super({
      urlBase: Skill.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });
  }

  public assign(response: any): Skill {
    super.assign(response);

    if (response) {
      if ('name' in response) {
        this.name = new InternationalizedField<string>(response.name);
      }
      if ('description' in response) {
        this.description = new InternationalizedField<string>(
          response.description
        );
      }
      if ('videos' in response) {
        this.videos = undefined;
        response.videos.forEach((video: any) => {
          this.video_objects.push(new Video().assign(video));
        });
      }
      if ('user_interface_elements' in response) {
        this.user_interface_elements = [];
        response.user_interface_elements.forEach((element: any) => {
          this.user_interface_elements.push(element);
        });
      }
    }

    return this;
  }

  public static async get(id: number): Promise<Skill> {
    return this.doGet({
      urlBase: Skill.URL_BASE,
      id,
      create: (response) => new Skill().assign(response)
    });
  }

  public static async list(): Promise<ApiList<Skill>> {
    const options: ListOptions<Skill> = {
      urlBase: Skill.URL_BASE,
      create: (response) => new Skill().assign(response)
    };
    return this.doList(options);
  }

  public static async listAll(): Promise<ApiList<Skill>> {
    return this.doList({
      urlBase: Skill.URL_BASE,
      create: (response) => new Skill().assign(response),
      all: true,
      parameters: {
        minimal: true
      },
      readFromCache: () => store.state.skillList,
      writeToCache: async (list: ApiList<Skill>) =>
        await store.commit(Mutation.CACHE_SKILL_LIST, list)
    });
  }

  public static async getSkillOfCourse(status: boolean): Promise<ApiList<Skill>> {
    const options: ListOptions<Skill> = {
        urlBase: Skill.URL_BASE,
        create: (response) => new Skill().assign(response),
        all: true,
        parameters: {
            is_skill_course: status,
            minimal: true,
        },
    };
    return this.doList(options);
  }

  public async delete(): Promise<void> {
    const id = this.id;
    await super.delete();

    // In case the entity is cached
    await store.commit(Mutation.DELETE_FROM_SKILL_LIST, id);
    bus.$emit(BusEvent.CACHE_CHANGED);
  }

  public async save(): Promise<number> {
    const isNew = !this.id;
    await this.saveInterfaceElements();
    const id = await super.save();

    if (isNew) {
      await store.commit(Mutation.ADD_TO_SKILL_LIST, this);
    } else {
      await this.saveVideos();
      await store.commit(Mutation.SKILL_UPDATED, this);
    }

    await super.save();
    bus.$emit(BusEvent.CACHE_CHANGED);

    return id;
  }

  public getName(): string {
    const name = this.name.getDefaultValue();
    if (name) {
      return name;
    }
    return '';
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }

  public async saveVideos() {
    for (const video of this.video_objects) {
      if (video.state === 'updated') {
        video.parent_id = this.id;
        video.parent_model = 'theme';
        if (!this.videos) {
          this.videos = [];
        }
        this.videos.push(await video.save());
      } else if (video.state === 'deleted' && video.id) {
        await video.delete();
      }
    }
  }

  public async saveInterfaceElements() {
    if (
      this.user_interface_elements &&
      this.user_interface_elements.length === 0
    ) {
      if (!this.id) {
        return delete this.user_interface_elements;
      } else {
        this.user_interface_elements = [];
      }
    }
  }
}
